/**
 * Who is signed in, and which community they are acting in.
 *
 * The community is held in one place and sent as a header on every request, because
 * `app.pow-wow.ca` serves every community from one hostname and the server re-checks membership on
 * every call. Putting it in the URL would make the path look like a tenant boundary, which is
 * exactly what §5 forbids — the path names a screen, never a community.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import type { Permission } from "@powwow/validation";
import { fetchMe, type CommunityMembership, type Me } from "./api.js";

const STORAGE_KEY = "pw.community";

/**
 * The community a platform admin has stepped into (D48), kept in `sessionStorage` so it lasts a
 * refresh but not a new tab — a powerful, temporary mode should not quietly outlive the window it
 * was opened in. It is a full `CommunityMembership` (with the owner permission set) because the
 * admin is not a member of it, so it can never come from `me.communities` the way an ordinary
 * selection does.
 */
const IMPERSONATION_KEY = "pw.impersonate";

function readImpersonation(): CommunityMembership | null {
  try {
    const raw = sessionStorage.getItem(IMPERSONATION_KEY);
    return raw ? (JSON.parse(raw) as CommunityMembership) : null;
  } catch {
    return null;
  }
}

/**
 * Arm impersonation from *outside* the provider — the platform console lives on its own route with
 * no `SessionProvider` around it, so "open this community's workspace" writes the target here and
 * then navigates to `/`, where the shell mounts, reads it back, and comes up already in that
 * community as the owner (D48).
 */
export function storeImpersonation(community: CommunityMembership): void {
  sessionStorage.setItem(IMPERSONATION_KEY, JSON.stringify(community));
}

interface SessionValue {
  me: Me | null;
  loading: boolean;
  community: CommunityMembership | null;
  setCommunity: (community: CommunityMembership) => void;
  reload: () => Promise<void>;
  can: (permission: Permission) => boolean;
  /** True while a platform admin is operating a community they are not a member of (D48). */
  impersonating: boolean;
  /** Step into a community's workspace as a platform admin, with the owner permission set. */
  enterImpersonation: (community: CommunityMembership) => void;
  /** Leave that mode and return to acting as oneself. */
  exitImpersonation: () => void;
}

const SessionContext = createContext<SessionValue | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const [me, setMe] = useState<Me | null>(null);
  const [loading, setLoading] = useState(true);
  const [communityId, setCommunityId] = useState<string | null>(() =>
    localStorage.getItem(STORAGE_KEY),
  );
  const [impersonated, setImpersonated] = useState<CommunityMembership | null>(readImpersonation);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const identity = await fetchMe();
      setMe(identity);
      if (identity.authenticated) {
        const remembered = identity.communities.find(
          (membership) => membership.id === localStorage.getItem(STORAGE_KEY),
        );
        // One membership means no choice to make. Several means asking, because putting someone in
        // the wrong community's dashboard is not obvious from the screen.
        const only = identity.communities.length === 1 ? identity.communities[0] : undefined;
        const chosen = remembered ?? only;
        if (chosen) {
          setCommunityId(chosen.id);
          localStorage.setItem(STORAGE_KEY, chosen.id);
        }
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  // Impersonation wins when active: the community it names is not one of the admin's memberships,
  // so it could never be found below, and while it is set it *is* the workspace on screen.
  const community =
    impersonated ??
    (me?.authenticated
      ? (me.communities.find((membership) => membership.id === communityId) ?? null)
      : null);

  const value = useMemo<SessionValue>(
    () => ({
      me,
      loading,
      community,
      setCommunity: (next) => {
        setCommunityId(next.id);
        localStorage.setItem(STORAGE_KEY, next.id);
      },
      reload,
      // A courtesy for deciding what to render, never a boundary — the server authorises every
      // action again regardless of what this returns.
      can: (permission) => community?.permissions.includes(permission) ?? false,
      impersonating: impersonated !== null,
      enterImpersonation: (next) => {
        sessionStorage.setItem(IMPERSONATION_KEY, JSON.stringify(next));
        setImpersonated(next);
      },
      exitImpersonation: () => {
        sessionStorage.removeItem(IMPERSONATION_KEY);
        setImpersonated(null);
      },
    }),
    [me, loading, community, impersonated, reload],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionValue {
  const value = useContext(SessionContext);
  if (!value) throw new Error("useSession must be used inside a SessionProvider");
  return value;
}

/** The community id for an API call. Throws rather than sending a request with no tenant. */
export function useCommunityId(): string {
  const { community } = useSession();
  if (!community) throw new Error("No community selected");
  return community.id;
}
